import React, { useState } from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton,
  Stack,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Chip,
  Button,
  Tooltip,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import DeviceMap from '../components/DeviceMap';
import DeviceList from '../components/DeviceList';
import { useDevices } from '../hooks/useDevices';
import { Device } from '../types';
import DeviceDetails from '../components/DeviceDetails';
import BulkUpdateModal from '../components/BulkUpdateModal';
import { 
  Smartphone as PhoneIcon,
  Map as MapIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  SignalCellularAlt as OnlineIcon,
  SignalCellularOff as OfflineIcon,
  MyLocation as LocationIcon,
  SystemUpdateAlt as UpdateIcon,
  Search as SearchIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ConfigMenu from '../components/ConfigMenu';

const Dashboard: React.FC = () => {
  const { signOut, user } = useAuth();
  const { devices, loading, error, requestUpdate, failedUpdates } = useDevices(!!user);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showInactive, setShowInactive] = useState<boolean>(false);
  const [showOnlineOnly, setShowOnlineOnly] = useState<boolean>(false);
  const [updateStatus, setUpdateStatus] = useState<{
    show: boolean;
    success: boolean;
    message: string;
  }>({
    show: false,
    success: false,
    message: ''
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const handleSelectDevice = (device: Device) => {
    setSelectedDevice(device);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  const handleOpenBulkUpdate = () => {
    setBulkUpdateOpen(true);
  };

  const handleCloseBulkUpdate = () => {
    setBulkUpdateOpen(false);
  };

  const handleRequestUpdate = async (deviceId: string) => {
    try {
      const success = await requestUpdate(deviceId);
      setUpdateStatus({
        show: true,
        success,
        message: success 
          ? 'Atualização solicitada com sucesso' 
          : 'Falha ao solicitar atualização'
      });
    } catch (err) {
      setUpdateStatus({
        show: true,
        success: false,
        message: 'Erro ao solicitar atualização'
      });
    }
  };

  // Filtrar dispositivos
  const filteredDevices = devices.filter(device => {
    // Filtro de dispositivos desativados
    // Quando showInactive é true, mostra APENAS desativados
    // Quando showInactive é false, mostra APENAS ativos
    if (showInactive) {
      if (device.isActive !== false) {
        return false; // Oculta os ativos
      }
    } else {
      if (device.isActive === false) {
        return false; // Oculta os desativados
      }
    }
    
    // Filtro de dispositivos online
    // Quando showOnlineOnly é true, mostra APENAS online
    if (showOnlineOnly) {
      if (!device.online) {
        return false; // Oculta os offline
      }
    }
    
    // Filtro de busca
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        (device.customName?.toLowerCase().includes(search)) ||
        (device.name?.toLowerCase().includes(search)) ||
        (device.companyName?.toLowerCase().includes(search)) ||
        (device.code?.toLowerCase().includes(search)) ||
        (device.id?.toLowerCase().includes(search))
      );
    }
    
    return true;
  });

  const onlineDevices = devices.filter(device => device.online).length;
  const offlineDevices = devices.length - onlineDevices;
  const inactiveDevices = devices.filter(device => device.isActive === false).length;
  const devicesComFalha = devices.filter(device => failedUpdates[device.id]).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static" elevation={1} sx={{ bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
            <DashboardIcon color="primary" />
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              INDECX Launcher Tracker
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography variant="subtitle1" color="text.secondary">
                Painel de Monitoramento de Dispositivos
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1}>
              <Tooltip title="Logs de Atualização">
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/updates-log')}
                  sx={{
                    '&:hover': {
                      bgcolor: 'primary.light',
                      color: 'white'
                    }
                  }}
                >
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
              <ConfigMenu />
              <IconButton
                color="inherit"
                onClick={() => signOut()}
                sx={{
                  '&:hover': {
                    bgcolor: 'error.light',
                    color: 'white'
                  }
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ flex: 1, py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
                Dashboard
              </Typography>
              
              <Tooltip title="Atualizar vários dispositivos de uma só vez">
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<UpdateIcon />}
                  onClick={handleOpenBulkUpdate}
                  sx={{ borderRadius: 8, px: 3 }}
                >
                  Atualização em Massa
                </Button>
              </Tooltip>
            </Box>

            {devicesComFalha > 0 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {devicesComFalha === 1
                  ? '1 dispositivo teve falha na última atualização enviada.'
                  : `${devicesComFalha} dispositivos tiveram falha na última atualização enviada.`}
                {' '}Abra o dispositivo para ver o motivo.
              </Alert>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                        <PhoneIcon sx={{ color: 'primary.main' }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" color="text.secondary">
                          Total de Dispositivos
                        </Typography>
                        <Typography variant="h4">
                          {devices.length}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                        <OnlineIcon sx={{ color: 'success.main' }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" color="text.secondary">
                          Dispositivos Online
                        </Typography>
                        <Typography variant="h4">
                          {onlineDevices}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                        <OfflineIcon sx={{ color: 'error.main' }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" color="text.secondary">
                          Dispositivos Offline
                        </Typography>
                        <Typography variant="h4">
                          {offlineDevices}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card sx={{ height: 'calc(100vh - 180px)' }}>
                  <CardHeader
                    avatar={
                      <LocationIcon color="primary" />
                    }
                    title="Localização dos Dispositivos"
                    subheader="Visualização em tempo real da distribuição geográfica"
                  />
                  <Divider />
                  <CardContent sx={{ height: 'calc(100vh - 240px)', p: 0 }}>
                    <DeviceMap devices={devices} onDeviceSelect={handleSelectDevice} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: 'calc(100vh - 180px)' }}>
                  <CardHeader
                    avatar={
                      <PhoneIcon color="primary" />
                    }
                    title="Lista de Dispositivos"
                    subheader={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {filteredDevices.length} {showInactive ? 'desativado' : 'ativo'}{filteredDevices.length !== 1 ? 's' : ''} de {devices.length} total
                        </Typography>
                      </Box>
                    }
                  />
                  <Divider />
                  
                  {/* Filtros */}
                  <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Stack spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Buscar por nome, empresa, código..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          bgcolor: 'white',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showOnlineOnly}
                            onChange={(e) => setShowOnlineOnly(e.target.checked)}
                            size="small"
                            color="success"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Exibir apenas online
                            {onlineDevices > 0 && (
                              <Chip 
                                label={onlineDevices} 
                                size="small" 
                                color="success"
                                sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                              />
                            )}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                            size="small"
                            color="error"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Exibir apenas desativados
                            {inactiveDevices > 0 && (
                              <Chip 
                                label={inactiveDevices} 
                                size="small" 
                                color="error"
                                sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                              />
                            )}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </Stack>
                  </Box>
                  
                  <Divider />
                  
                  <CardContent sx={{ p: 0, height: 'calc(100vh - 380px)', overflow: 'auto' }}>
                    {filteredDevices.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm 
                            ? 'Nenhum dispositivo encontrado com esses critérios'
                            : 'Nenhum dispositivo ativo encontrado'}
                        </Typography>
                      </Box>
                    ) : (
                      <DeviceList 
                        devices={filteredDevices} 
                        onDeviceSelect={handleSelectDevice}
                        onRequestUpdate={handleRequestUpdate}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Container>

      <DeviceDetails
        open={detailsOpen}
        onClose={handleCloseDetails}
        device={selectedDevice}
        onRequestUpdate={handleRequestUpdate}
      />

      <BulkUpdateModal
        open={bulkUpdateOpen}
        onClose={handleCloseBulkUpdate}
        devices={devices}
      />

      <Snackbar
        open={updateStatus.show}
        autoHideDuration={5000}
        onClose={() => setUpdateStatus({ ...updateStatus, show: false })}
      >
        <Alert 
          severity={updateStatus.success ? 'success' : 'error'} 
          sx={{ width: '100%' }}
        >
          {updateStatus.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;